import { forwardRef, useEffect, useRef, useState } from "react";
import { Check, X, Save, RefreshCw, Send, Trash2, Loader2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, apiError, formatBRT } from "@/lib/api";
import { StatusBadge, ResultLine } from "@/components/staff/StatusBadge";
import { NETWORK_LABELS } from "@/components/staff/NetworkStatus";
import { TOPIC_LABELS, SERVER_LABELS } from "@/components/staff/GenerateDialog";
import { ConfirmAction } from "@/components/staff/ConfirmAction";

const Action = forwardRef(({ onClick, icon: Icon, children, testId, variant = "ghost", disabled, ...rest }, ref) => (
  <button ref={ref} onClick={onClick} disabled={disabled} data-testid={testId} {...rest}
    className={`flex items-center gap-1.5 px-3 py-2 text-sm dz-heading uppercase tracking-wider font-bold transition-colors disabled:opacity-40 ${
      variant === "primary" ? "bg-[#c81e1e] text-white hover:brightness-110" : "border border-white/15 text-zinc-300 hover:border-white/40 hover:text-white"}`}>
    <Icon size={15} /> {children}
  </button>
));

export const PostCard = ({ post, reload }) => {
  const [title, setTitle] = useState(post.title);
  const [caption, setCaption] = useState(post.caption);
  const [networks, setNetworks] = useState(post.networks);
  const [busy, setBusy] = useState(false);
  const id = post.id;
  const editable = !["published", "publishing", "generating"].includes(post.status);
  const dirty = title !== post.title || caption !== post.caption || networks.join() !== post.networks.join();

  const netKey = post.networks.join();
  const server = useRef({ title: post.title, caption: post.caption, networks: netKey });
  useEffect(() => {
    const prev = server.current;
    setTitle((cur) => (cur === prev.title ? post.title : cur));
    setCaption((cur) => (cur === prev.caption ? post.caption : cur));
    setNetworks((cur) => (cur.join() === prev.networks ? post.networks : cur));
    server.current = { title: post.title, caption: post.caption, networks: netKey };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.title, post.caption, netKey]);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      if (msg) toast.success(msg);
      await reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  const save = () => api.patch(`/posts/${id}`, { title, caption, networks });
  const toggle = (n) => setNetworks((cur) => (cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n]));

  return (
    <article className="border border-white/10 bg-[#121314] flex flex-col sm:flex-row" data-testid={`post-card-${id}`}>
      <div className="sm:w-56 shrink-0 aspect-square sm:self-start bg-black/50 grid place-items-center">
        {post.image_url ? <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" data-testid={`post-image-${id}`} />
          : post.status === "generating" ? <Loader2 className="animate-spin text-[#e02424]" /> : <ImageIcon className="text-zinc-600" />}
      </div>
      <div className="flex-1 p-5 space-y-3 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={post.status} testId={`post-status-${id}`} />
          <span className="dz-mono text-[11px] text-zinc-400 uppercase">{TOPIC_LABELS[post.topic]} · {SERVER_LABELS[post.server]}</span>
          {post.source === "autopilot" && <span className="dz-mono text-[11px] text-[#f87171] uppercase">Auto</span>}
        </div>
        <p className="dz-mono text-xs text-zinc-300" data-testid={`post-schedule-${id}`}>
          {post.status === "published" ? `Publicado: ${formatBRT(post.published_at)}` : `Horário: ${formatBRT(post.scheduled_for)}`}
        </p>
        {post.error && <p className="text-xs text-red-400" data-testid={`post-error-${id}`}>{post.error}</p>}
        {post.status === "generating" ? <p className="text-sm text-zinc-400">A IA está escrevendo o texto e criando a imagem...</p> : (
          <>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} disabled={!editable} maxLength={90}
              className="rounded-none bg-black/40 border-white/15 text-white font-semibold" data-testid={`post-title-input-${id}`} />
            <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} disabled={!editable} rows={7}
              className="rounded-none bg-black/40 border-white/15 text-zinc-200 text-sm" data-testid={`post-caption-input-${id}`} />
            <div className="flex flex-wrap gap-4">
              {Object.entries(NETWORK_LABELS).map(([n, label]) => (
                <label key={n} className="flex items-center gap-2 text-xs text-zinc-300">
                  <Checkbox checked={networks.includes(n)} onCheckedChange={() => toggle(n)} disabled={!editable} data-testid={`post-network-${n}-${id}`}
                    className="border-zinc-400 data-[state=checked]:bg-[#c81e1e] data-[state=checked]:border-[#c81e1e] data-[state=checked]:text-white" />
                  {label}
                </label>
              ))}
            </div>
          </>
        )}
        {Object.keys(post.results || {}).length > 0 && (
          <div className="border-t border-white/10 pt-3 space-y-1">
            {Object.entries(post.results).map(([n, r]) => <ResultLine key={n} network={n} label={NETWORK_LABELS[n]} result={r} />)}
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {["pending", "missed", "rejected"].includes(post.status) && (
            <Action variant="primary" icon={Check} testId={`approve-post-${id}`} disabled={busy}
              onClick={() => run(async () => { if (dirty) await save(); await api.post(`/posts/${id}/approve`); }, "Post aprovado e agendado")}>Aprovar</Action>
          )}
          {editable && dirty && <Action icon={Save} testId={`save-post-${id}`} disabled={busy} onClick={() => run(save, "Alterações salvas")}>Salvar</Action>}
          {["pending", "approved", "failed", "missed"].includes(post.status) && post.image_url && (
            <ConfirmAction testId={`publish-now-${id}`} title="Postar agora?" confirmLabel="Postar agora"
              description="O post será publicado imediatamente em todas as redes marcadas e conectadas."
              onConfirm={() => run(async () => { if (dirty) await save(); await api.post(`/posts/${id}/publish-now`); }, "Publicando agora...")}
              trigger={<Action icon={Send} testId={`publish-now-post-${id}`} disabled={busy}>Postar agora</Action>} />
          )}
          {editable && post.status !== "approved" && (
            <>
              <Action icon={RefreshCw} testId={`regenerate-image-${id}`} disabled={busy} onClick={() => run(() => api.post(`/posts/${id}/regenerate?part=image`), "Gerando nova imagem...")}>Nova imagem</Action>
              <Action icon={RefreshCw} testId={`regenerate-text-${id}`} disabled={busy} onClick={() => run(() => api.post(`/posts/${id}/regenerate?part=text`), "Gerando novo texto...")}>Novo texto</Action>
            </>
          )}
          {["pending", "approved", "missed", "failed"].includes(post.status) && (
            <Action icon={X} testId={`reject-post-${id}`} disabled={busy} onClick={() => run(() => api.post(`/posts/${id}/reject`), "Post rejeitado")}>Rejeitar</Action>
          )}
          {post.status !== "publishing" && (
            <ConfirmAction testId={`delete-${id}`} title="Excluir post?" confirmLabel="Excluir"
              description="Esta ação não pode ser desfeita."
              onConfirm={() => run(() => api.delete(`/posts/${id}`), "Post excluído")}
              trigger={<Action icon={Trash2} testId={`delete-post-${id}`} disabled={busy}>Excluir</Action>} />
          )}
        </div>
      </div>
    </article>
  );
};
